import { useMemo, useState } from "react";
import useRecommendedIds from "../../hooks/useRecommendedIds";
import { trackInteraction } from "../../lib/trackInteraction";
import { SUBCATEGORY_SPEC_FIELDS } from "../../data/specFields";
import type { Product, ProductCategory } from "../../types";
import {
  applySortOption,
  matchesNumeric,
  matchesSelect,
  sortProducts,
  type CatalogFilterInit,
  type NumericFilterValue,
  type SortOption,
} from "./filterUtils";

const EMPTY_INIT: CatalogFilterInit = {
  category: "All",
  subcategory: null,
  brands: [],
  inStockOnly: false,
  numericFilters: {},
  selectFilters: {},
  sort: "relevance",
};

/**
 * Category/subcategory/brand/characteristic filtering + sorting, shared by
 * the full catalog page and the homepage catalog preview so both present the
 * exact same facet system rather than two divergent implementations.
 *
 * `init` seeds the initial state (e.g. parsed from URL search params on
 * mount) — like React's useState, only the first render's value is used.
 */
export default function useCatalogFilters(products: Product[], init: CatalogFilterInit = EMPTY_INIT) {
  const recommendedIds = useRecommendedIds();

  const [activeCategory, setActiveCategoryState] = useState<ProductCategory | "All">(init.category);
  const [activeSubcategory, setActiveSubcategoryState] = useState<string | null>(init.subcategory);
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(() => new Set(init.brands));
  const [numericFilters, setNumericFilters] = useState<Record<string, NumericFilterValue>>(
    init.numericFilters
  );
  const [selectFilters, setSelectFilters] = useState<Record<string, Set<string>>>(init.selectFilters);
  const [inStockOnly, setInStockOnly] = useState(init.inStockOnly);
  const [sortBy, setSortBy] = useState<SortOption>(init.sort);

  const resetDownstream = () => {
    setSelectedBrands(new Set());
    setNumericFilters({});
    setSelectFilters({});
  };

  const setActiveCategory = (category: ProductCategory | "All") => {
    // Clicking the already-active category again collapses it back to "All"
    // instead of re-selecting itself — otherwise there's no way to close the
    // subcategory dropdown once it's open.
    const next = category === activeCategory ? "All" : category;
    setActiveCategoryState(next);
    setActiveSubcategoryState(null);
    resetDownstream();
    if (next !== "All") trackInteraction("category_view", next);
  };

  const setSubcategory = (subcategory: string | null) => {
    setActiveSubcategoryState((prev) => (prev === subcategory ? null : subcategory));
    resetDownstream();
  };

  const clearAll = () => {
    setActiveCategoryState("All");
    setActiveSubcategoryState(null);
    setInStockOnly(false);
    resetDownstream();
  };

  const toggleBrand = (brand: string) => {
    setSelectedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) next.delete(brand);
      else next.add(brand);
      return next;
    });
  };

  const setNumericFilter = (key: string, value: NumericFilterValue | undefined) => {
    setNumericFilters((prev) => {
      const next = { ...prev };
      if (!value || (value.min === undefined && value.max === undefined)) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const toggleSelectOption = (key: string, option: string) => {
    setSelectFilters((prev) => {
      const next = { ...prev };
      const current = new Set(next[key] ?? []);
      if (current.has(option)) current.delete(option);
      else current.add(option);
      if (current.size === 0) delete next[key];
      else next[key] = current;
      return next;
    });
  };

  const categoryCounts = useMemo(() => {
    const counts: Partial<Record<ProductCategory, number>> = {};
    for (const p of products) counts[p.category] = (counts[p.category] ?? 0) + 1;
    return counts;
  }, [products]);

  const subcategoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    if (activeCategory === "All") return counts;
    for (const p of products) {
      if (p.category !== activeCategory || !p.subcategory) continue;
      counts[p.subcategory] = (counts[p.subcategory] ?? 0) + 1;
    }
    return counts;
  }, [products, activeCategory]);

  const categoryFiltered =
    activeCategory === "All" ? products : products.filter((p) => p.category === activeCategory);

  const subcategoryFiltered = activeSubcategory
    ? categoryFiltered.filter((p) => p.subcategory === activeSubcategory)
    : categoryFiltered;

  const specFieldDefs = activeSubcategory ? SUBCATEGORY_SPEC_FIELDS[activeSubcategory] ?? [] : [];

  const inStockCount = subcategoryFiltered.filter(
    (p) =>
      p.stockQuantity !== undefined &&
      p.stockQuantity > 0 &&
      matchesNumeric(p, numericFilters) &&
      matchesSelect(p, selectFilters)
  ).length;

  const stockFiltered = inStockOnly
    ? subcategoryFiltered.filter((p) => p.stockQuantity !== undefined && p.stockQuantity > 0)
    : subcategoryFiltered;

  const brandOptions = useMemo(
    () =>
      Array.from(new Set(stockFiltered.map((p) => p.brand).filter((b): b is string => !!b))).sort(
        (a, b) => a.localeCompare(b)
      ),
    [stockFiltered]
  );

  const brandCounts: Record<string, number> = {};
  for (const p of stockFiltered) {
    if (!p.brand || !matchesNumeric(p, numericFilters) || !matchesSelect(p, selectFilters)) continue;
    brandCounts[p.brand] = (brandCounts[p.brand] ?? 0) + 1;
  }

  const brandFiltered =
    selectedBrands.size > 0
      ? stockFiltered.filter((p) => p.brand && selectedBrands.has(p.brand))
      : stockFiltered;

  const numericBounds = useMemo(() => {
    const bounds: Record<string, { min: number; max: number }> = {};
    for (const field of specFieldDefs) {
      if (field.type !== "number") continue;
      const values = brandFiltered
        .map((p) => p.characteristics?.[field.key])
        .filter((v): v is number => typeof v === "number");
      if (values.length === 0) continue;
      bounds[field.key] = { min: Math.min(...values), max: Math.max(...values) };
    }
    return bounds;
  }, [specFieldDefs, brandFiltered]);

  const selectOptions = useMemo(() => {
    const options: Record<string, string[]> = {};
    for (const field of specFieldDefs) {
      if (field.type !== "select") continue;
      const values = new Set(
        brandFiltered
          .map((p) => p.characteristics?.[field.key])
          .filter((v): v is string => typeof v === "string" && v !== "")
      );
      if (values.size === 0) continue;
      options[field.key] = Array.from(values).sort((a, b) => a.localeCompare(b));
    }
    return options;
  }, [specFieldDefs, brandFiltered]);

  const selectOptionCounts = useMemo(() => {
    const counts: Record<string, Record<string, number>> = {};
    for (const [key, options] of Object.entries(selectOptions)) {
      const base = brandFiltered.filter(
        (p) => matchesNumeric(p, numericFilters) && matchesSelect(p, selectFilters, key)
      );
      counts[key] = {};
      for (const option of options) {
        counts[key][option] = base.filter((p) => p.characteristics?.[key] === option).length;
      }
    }
    return counts;
  }, [selectOptions, brandFiltered, numericFilters, selectFilters]);

  const characteristicFiltered = brandFiltered.filter(
    (p) => matchesNumeric(p, numericFilters) && matchesSelect(p, selectFilters)
  );

  // "Relevance" keeps the existing recommendation-biased order; every other
  // sort option replaces it outright (see applySortOption in filterUtils).
  const displayed =
    sortBy === "relevance"
      ? sortProducts(characteristicFiltered, recommendedIds)
      : applySortOption(characteristicFiltered, sortBy);

  const activeFilterCount =
    (activeSubcategory ? 1 : 0) +
    (inStockOnly ? 1 : 0) +
    selectedBrands.size +
    Object.keys(numericFilters).length +
    Object.values(selectFilters).reduce((sum, s) => sum + s.size, 0);

  return {
    activeCategory,
    setActiveCategory,
    activeSubcategory,
    setSubcategory,
    inStockOnly,
    setInStockOnly,
    inStockCount,
    sortBy,
    setSortBy,
    selectedBrands,
    toggleBrand,
    numericFilters,
    setNumericFilter,
    selectFilters,
    toggleSelectOption,
    clearAll,
    categoryCounts,
    subcategoryCounts,
    brandOptions,
    brandCounts,
    specFieldDefs,
    numericBounds,
    selectOptions,
    selectOptionCounts,
    displayed,
    activeFilterCount,
    totalCount: products.length,
  };
}
